/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Activity, Lap } from '../shared/models';

interface PatternResult {
  analysis: string;
  suggestions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class HelperService {
  colorMap = new Map<string, string>([
    ['mediumvioletred', '#ffffff'],
    ['deeppink', '#ffffff'],
    ['palevioletred', '#000000'],
    ['hotpink', '#ffffff'],
    ['lightpink', '#000000'],
    ['pink', '#000000'],
    ['darkred', '#ffffff'],
    ['red', '#ffffff'],
    ['firebrick', '#ffffff'],
    ['crimson', '#ffffff'],
    ['indianred', '#ffffff'],
    ['lightcoral', '#000000'],
    ['salmon', '#000000'],
    ['darksalmon', '#ffffff'],
    ['lightsalmon', '#000000'],
    ['orangered', '#ffffff'],
    ['tomato', '#000000'],
    ['darkorange', '#ffffff'],
    ['coral', '#000000'],
    ['orange', '#000000'],
    ['darkkhaki', '#000000'],
    ['gold', '#000000'],
    ['khaki', '#000000'],
    ['peachpuff', '#000000'],
    ['yellow', '#000000'],
    ['palegoldenrod', '#000000'],
    ['moccasin', '#000000'],
    ['papayawhip', '#000000'],
    ['lightgoldenrodyellow', '#000000'],
    ['lemonchiffon', '#000000'],
    ['lightyellow', '#000000'],
    ['maroon', '#ffffff'],
    ['brown', '#ffffff'],
    ['saddlebrown', '#ffffff'],
    ['sienna', '#ffffff'],
    ['chocolate', '#ffffff'],
    ['darkgoldenrod', '#ffffff'],
    ['peru', '#ffffff'],
    ['rosybrown', '#000000'],
    ['goldenrod', '#000000'],
    ['sandybrown', '#000000'],
    ['tan', '#000000'],
    ['burlywood', '#000000'],
    ['wheat', '#000000'],
    ['navajowhite', '#000000'],
    ['bisque', '#000000'],
    ['blanchedalmond', '#000000'],
    ['cornsilk', '#000000'],
    ['darkgreen', '#000000'],
    ['green', '#ffffff'],
    ['darkolivegreen', '#ffffff'],
    ['forestgreen', '#ffffff'],
    ['seagreen', '#ffffff'],
    ['olive', '#ffffff'],
    ['olivedrab', '#ffffff'],
    ['mediumseagreen', '#ffffff'],
    ['limegreen', '#ffffff'],
    ['lime', '#000000'],
    ['springgreen', '#000000'],
    ['mediumspringgreen', '#ffffff'],
    ['darkseagreen', '#ffffff'],
    ['mediumaquamarine', '#ffffff'],
    ['yellowgreen', '#ffffff'],
    ['lawngreen', '#000000'],
    ['chartreuse', '#000000'],
    ['lightgreen', '#000000'],
    ['greenyellow', '#000000'],
    ['palegreen', '#000000'],
    ['teal', '#ffffff'],
    ['darkcyan', '#ffffff'],
    ['lightseagreen', '#ffffff'],
    ['cadellue', '#ffffff'],
    ['darkturquoise', '#000000'],
    ['mediumturquoise', '#000000'],
    ['turquoise', '#000000'],
    ['aqua', '#000000'],
    ['cyan', '#000000'],
    ['aquamarine', '#000000'],
    ['paleturquoise', '#000000'],
    ['lightcyan', '#000000'],
    ['navy', '#ffffff'],
    ['darkblue', '#ffffff'],
    ['mediumblue', '#ffffff'],
    ['blue', '#ffffff'],
    ['midnightblue', '#ffffff'],
    ['royalblue', '#ffffff'],
    ['steelblue', '#ffffff'],
    ['dodgerblue', '#ffffff'],
    ['deepskyblue', '#000000'],
    ['cornflowerblue', '#ffffff'],
    ['skyblue', '#000000'],
    ['lightskyblue', '#000000'],
    ['lightsteelblue', '#000000'],
    ['lightblue', '#000000'],
    ['powderblue', '#000000'],
    ['indigo', '#ffffff'],
    ['purple', '#ffffff'],
    ['darkmagenta', '#ffffff'],
    ['darkviolet', '#ffffff'],
    ['darkslateblue', '#ffffff'],
    ['blueviolet', '#ffffff'],
    ['darkorchid', '#ffffff'],
    ['fuchsia', '#ffffff'],
    ['magenta', '#ffffff'],
    ['slateblue', '#ffffff'],
    ['mediumslateblue', '#ffffff'],
    ['mediumorchid', '#ffffff'],
    ['mediumpurple', '#ffffff'],
    ['orchid', '#ffffff'],
    ['violet', '#ffffff'],
    ['plum', '#ffffff'],
    ['thistle', '#000000'],
    ['lavender', '#000000'],
    ['mistyrose', '#000000'],
    ['antiquewhite', '#000000'],
    ['linen', '#000000'],
    ['beige', '#000000'],
    ['whitesmoke', '#000000'],
    ['lavenderblush', '#000000'],
    ['oldlace', '#000000'],
    ['aliceblue', '#000000'],
    ['seashell', '#000000'],
    ['ghostwhite', '#000000'],
    ['honeydew', '#000000'],
    ['foralwhite', '#000000'],
    ['azure', '#000000'],
    ['mintcream', '#000000'],
    ['snow', '#000000'],
    ['ivory', '#000000'],
    ['white', '#000000'],
    ['black', '#ffffff'],
    ['darkslategray', '#ffffff'],
    ['dimgray', '#ffffff'],
    ['slategrey', '#ffffff'],
    ['gray', '#ffffff'],
    ['lightslategray', '#ffffff'],
    ['darkgray', '#ffffff'],
    ['silver', '#000000'],
    ['lightgray', '#000000'],
    ['gainsboro', '#000000'],
  ]);

  constructor() {}

  saveFile(base64data: string) {
    try {
      console.log('Base64 data:', base64data);
      console.log('File saved successfully to Pictures directory');
    } catch (error) {
      console.error('Error saving file:', error);
    }
  }

  lightenColor(color) {
    // If the input color is in hex format (#RRGGBB), convert it to RGB
    if (color[0] === '#') {
      color = color.slice(1); // Remove the '#'
    }
    color = parseInt(color, 16); // Convert to decimal
    // eslint-disable-next-line no-bitwise
    color = [(color >> 16) & 255, (color >> 8) & 255, color & 255]; // Split into RGB components
    // Adjust each RGB component to lighten the color
    color = color.map((component) =>
      Math.round(component + (255 - component) * 0.5)
    );
    // Convert the RGB values back to hex format
    color = color.reduce(
      (acc, component) => acc + component.toString(16).padStart(2, '0'),
      '#'
    );
    return color;
  }

  trimCanvas(canvas): HTMLCanvasElement {
    const context = canvas.getContext('2d');

    const topLeft = {
      x: canvas.width,
      y: canvas.height,
      update(x, y) {
        this.x = Math.min(this.x, x);
        this.y = Math.min(this.y, y);
      },
    };

    const bottomRight = {
      x: 0,
      y: 0,
      update(x, y) {
        this.x = Math.max(this.x, x);
        this.y = Math.max(this.y, y);
      },
    };

    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);

    for (let x = 0; x < canvas.width; x++) {
      for (let y = 0; y < canvas.height; y++) {
        const alpha = imageData.data[y * (canvas.width * 4) + x * 4 + 3];
        if (alpha !== 0) {
          topLeft.update(x, y);
          bottomRight.update(x, y);
        }
      }
    }

    const width = bottomRight.x - topLeft.x;
    const height = bottomRight.y - topLeft.y;

    const croppedCanvas = context.getImageData(
      topLeft.x,
      topLeft.y,
      width,
      height
    );
    canvas.width = width;
    canvas.height = height;
    context.putImageData(croppedCanvas, 0, 0);
    return canvas;
  }

  beautifyDistance(activity: Activity): string {
    const type = activity.type;
    const distance = activity.distance;
    const duration = activity.moving_time;

    if (type === 'Run' || type === 'VirtualRun') {
      if (distance >= 21092 && distance < 21500) {
        return 'Half Marathon';
      } else if (distance >= 42192 && distance < 44000) {
        return 'Marathon';
      } else if (distance >= 50000) {
        const remainder = distance % 1000;
        if (remainder > 0) {
          return 'ULTRA ' + Math.floor(distance / 1000) + '+';
        } else {
          return 'ULTRA ' + Math.floor(distance / 1000);
        }
      } else {
        return Math.round(distance / 1000) + 'K';
      }
    } else if (type === 'Ride' || type === 'VirtualRide') {
      if (distance >= 1000 && distance < 120000) {
        const remainder = distance % 1000;
        if (remainder > 0) {
          return Math.floor(distance / 1000) + '+' + 'km';
        } else {
          return Math.floor(distance / 1000) + 'km';
        }
      } else if (distance >= 120000 && distance < 200000) {
        return 'Gran Fondo ' + Math.floor(distance / 1000) + 'km';
      } else if (distance >= 200000) {
        return 'Brevet ' + Math.floor(distance / 1000) + 'km';
      } else {
        return distance + 'm';
      }
    } else if (type === 'Swim') {
      if (distance >= 10000) {
        const remainder = distance % 1000;
        if (remainder > 0) {
          return Math.floor(distance / 1000) + '+' + 'km';
        } else {
          return Math.floor(distance / 1000) + 'km';
        }
      } else {
        return distance + 'm';
      }
    } else {
      if (distance > 0) {
        if (distance < 1000) {
          return distance + 'm';
        } else {
          return distance / 1000 + 'km';
        }
      } else {
        return this.transform(activity.moving_time);
      }
    }
  }

  generateSuggestions(activity: Activity): PatternResult {
    const {
      type,
      distance,
      moving_time,
      total_elevation_gain,
      average_speed,
      laps,
    } = activity;

    const movingTimeMin = Math.round(moving_time / 60);
    const elevationM = Math.round(total_elevation_gain || 0);
    const paceMinPerKm =
      type === 'Swim' ? null : Number((1000 / average_speed / 60).toFixed(2));
    const distanceKm = Math.round(distance / 1000);

    // Filter and merge laps into significant efforts
    const significantLaps: Lap[] = [];
    const filteredLaps = laps.filter(
      (lap) => lap.distance >= 50 && lap.average_speed > 1
    );

    for (let i = 0; i < filteredLaps.length; i++) {
      const currentLap = { ...filteredLaps[i] };
      while (i + 1 < filteredLaps.length) {
        const nextLap = filteredLaps[i + 1];
        const currentPace = Number(
          (1000 / currentLap.average_speed / 60).toFixed(2)
        );
        const nextPace = Number((1000 / nextLap.average_speed / 60).toFixed(2));
        const paceDiff = Math.abs(currentPace - nextPace);

        if (paceDiff < 0.5 && currentLap.distance + nextLap.distance <= 3000) {
          currentLap.distance += nextLap.distance;
          currentLap.moving_time += nextLap.moving_time;
          currentLap.total_elevation_gain =
            (currentLap.total_elevation_gain || 0) +
            (nextLap.total_elevation_gain || 0);
          currentLap.average_speed =
            currentLap.distance / currentLap.moving_time;
          i++;
        } else {
          break;
        }
      }
      significantLaps.push(currentLap);
      console.log(
        `Merged Lap: distance=${currentLap.distance}, pace=${Number(
          (1000 / currentLap.average_speed / 60).toFixed(2)
        )}`
      );
    }

    // Create efforts array
    const efforts = significantLaps.map((lap) => ({
      distance: Number((lap.distance / 1000).toFixed(2)),
      pace:
        type !== 'Swim'
          ? Number((1000 / lap.average_speed / 60).toFixed(2))
          : null,
      time: lap.moving_time,
      elevation: lap.total_elevation_gain || 0,
      lapCount: 1,
    }));

    // Group efforts by distance to find patterns
    const distanceMap = new Map<
      number,
      { count: number; efforts: typeof efforts }
    >();
    efforts.forEach((effort) => {
      let foundKey = false;
      for (const [key, value] of distanceMap) {
        if (Math.abs(key - effort.distance) < 0.1) {
          value.count += 1;
          value.efforts.push(effort);
          foundKey = true;
          break;
        }
      }
      if (!foundKey) {
        distanceMap.set(effort.distance, { count: 1, efforts: [effort] });
      }
    });

    // Identify significant patterns (count >= 2 or explicit repeats)
    const patterns = [];
    for (const [
      patternDistance,
      { count, efforts: patternEfforts },
    ] of distanceMap) {
      if (
        count >= 2 ||
        (patternDistance >= 0.9 && patternDistance <= 1.1 && count >= 5)
      ) {
        patterns.push({
          distance: patternDistance,
          count,
          avgPace:
            patternEfforts.reduce((sum, e) => sum + (e.pace || 0), 0) / count,
          avgElevation:
            patternEfforts.reduce((sum, e) => sum + e.elevation, 0) / count,
        });
      }
    }
    console.log('Patterns:', patterns);

    let analysis = '';
    let suggestions: string[] = [];

    if (patterns.length > 0) {
      analysis =
        patterns
          .map((p) => {
            let patternType = 'Steady';
            if (
              p.distance <= 0.5 &&
              (p.avgPace || 0) < (paceMinPerKm || 0) * 0.7
            )
              {patternType = 'Sprints';}
            else if (
              p.distance <= 5 &&
              (p.avgPace || 0) < (paceMinPerKm || 0) * 0.9
            )
              {patternType = 'Intervals';}
            else if (p.count >= 3 && p.avgElevation > 20)
              {patternType = 'Hill Repeats';}

            if (type === 'Run') {
              if (patternType === 'Sprints')
                {return `${p.count} sprint efforts of ${
                  p.distance
                } km at ${p.avgPace.toFixed(2)} min/km`;}
              else if (patternType === 'Intervals')
                {return `${p.count} interval efforts of ${
                  p.distance
                } km at ${p.avgPace.toFixed(2)} min/km`;}
              else if (patternType === 'Hill Repeats')
                {return `${p.count} hill repeats of ${
                  p.distance
                } km averaging ${Math.round(p.avgElevation)} m elevation`;}
              else
                {return `${p.count} steady efforts of ${
                  p.distance
                } km at ${p.avgPace.toFixed(2)} min/km`;}
            }
            return '';
          })
          .join(' and ') +
        `, completed in ${movingTimeMin} minutes with ${elevationM} m total elevation.`;

      suggestions = [];
      for (let i = 0; i < patterns.length && suggestions.length < 3; i++) {
        const p = patterns[i];
        let patternType = 'Steady';
        if (p.distance <= 0.5 && (p.avgPace || 0) < (paceMinPerKm || 0) * 0.7)
          {patternType = 'Sprints';}
        else if (
          p.distance <= 5 &&
          (p.avgPace || 0) < (paceMinPerKm || 0) * 0.9
        )
          {patternType = 'Intervals';}
        else if (p.count >= 3 && p.avgElevation > 20)
          {patternType = 'Hill Repeats';}

        if (type === 'Run') {
          if (patternType === 'Sprints') {
            suggestions.push(`${p.count}x${p.distance} km Sprints`);
            if (suggestions.length < 3)
              {suggestions.push(`Sprint: ${p.avgPace.toFixed(2)} min/km`);}
          } else if (patternType === 'Intervals') {
            suggestions.push(`${p.count}x${p.distance} km Intervals`);
            if (suggestions.length < 3)
              {suggestions.push(`Intervals: ${p.avgPace.toFixed(2)} min/km`);}
          } else if (patternType === 'Hill Repeats') {
            suggestions.push(
              `${p.count}x${Math.round(p.avgElevation)} m Hill Repeats`
            );
            if (suggestions.length < 3)
              {suggestions.push(`Hill Efforts: ${p.distance} km`);}
          } else {
            suggestions.push(`${p.count}x${p.distance} km Efforts`);
            if (suggestions.length < 3)
              {suggestions.push(`${p.avgPace.toFixed(2)} min/km Pace`);}
          }
        }
      }
    } else {
      if (type === 'Run') {
        let workoutType = 'Steady';
        if (Math.abs(distance - 5000) < 0.5) {workoutType = '5K';}
        else if (Math.abs(distance - 8045) < 0.5) {workoutType = '5 Miles';}
        else if (Math.abs(distance - 10000) < 0.5) {workoutType = '10K';}
        else if (Math.abs(distance - 16090) < 0.5) {workoutType = '10 Miles';}
        else if (Math.abs(distance - 21097.5) < 0.5)
          {workoutType = 'Half Marathon';}
        else if (Math.abs(distance - 42195) < 0.5) {workoutType = 'Marathon';}
        else if (distance > 20000) {workoutType = 'Long Run';}
        else if (
          distance >= 5000 &&
          distance <= 15000 &&
          efforts.length > 0 &&
          (efforts[0].pace || 0) < (paceMinPerKm || 0) * 0.95
        )
          {workoutType = 'Tempo';}

        const avgEffortPace =
          efforts.length > 0 ? efforts[0].pace : paceMinPerKm;

        if (workoutType === '5K')
          {analysis = `A 5K run, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === '5 Miles')
          {analysis = `A 5-mile run, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === '10K')
          {analysis = `A 10K run, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === '10 Miles')
          {analysis = `A 10-mile run, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === 'Half Marathon')
          {analysis = `A half marathon, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === 'Marathon')
          {analysis = `A marathon, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km with ${elevationM} m elevation.`;}
        else if (workoutType === 'Long Run')
          {analysis = `A long run of ${distanceKm} km with ${elevationM} m elevation, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km.`;}
        else if (workoutType === 'Tempo')
          {analysis = `A tempo run of ${distanceKm} km at ${(
            avgEffortPace || paceMinPerKm
          ).toFixed(
            2
          )} min/km, completed in ${movingTimeMin} minutes with ${elevationM} m elevation.`;}
        else
          {analysis = `A steady run of ${distanceKm} km with ${elevationM} m elevation, completed in ${movingTimeMin} minutes at ${paceMinPerKm.toFixed(
            2
          )} min/km.`;}

        if (workoutType === '5K')
          {suggestions = [
            `5K Race: ${distanceKm} km`,
            `${movingTimeMin}-Minute 5K`,
            `${paceMinPerKm.toFixed(2)} min/km Effort`,
          ];}
        else if (workoutType === '5 Miles')
          {suggestions = [
            `5-Mile Race: ${distanceKm} km`,
            `${movingTimeMin}-Minute 5 Miles`,
            `${elevationM} m 5-Miler`,
          ];}
        else if (workoutType === '10K')
          {suggestions = [
            `10K Race: ${distanceKm} km`,
            `${movingTimeMin}-Minute 10K`,
            `${paceMinPerKm.toFixed(2)} min/km 10K`,
          ];}
        else if (workoutType === '10 Miles')
          {suggestions = [
            `10-Mile Race: ${distanceKm} km`,
            `${movingTimeMin}-Minute 10 Miles`,
            `${elevationM} m 10-Miler`,
          ];}
        else if (workoutType === 'Half Marathon')
          {suggestions = [
            `Half Marathon: ${distanceKm} km`,
            `${movingTimeMin}-Minute Half`,
            `${paceMinPerKm.toFixed(2)} min/km Half`,
          ];}
        else if (workoutType === 'Marathon')
          {suggestions = [
            `Marathon: ${distanceKm} km`,
            `${movingTimeMin}-Minute Marathon`,
            `${elevationM} m Marathon`,
          ];}
        else if (workoutType === 'Long Run')
          {suggestions = [
            `Long Run: ${distanceKm} km`,
            `${movingTimeMin}-Minute LSD`,
            `${elevationM} m Elevation`,
          ];}
        else if (workoutType === 'Tempo')
          {suggestions = [
            `Tempo: ${distanceKm} km`,
            `${movingTimeMin}-Minute Tempo`,
            `${(avgEffortPace || paceMinPerKm).toFixed(2)} min/km Effort`,
          ];}
        else
          {suggestions = [
            `${distanceKm} km Steady Run`,
            `${paceMinPerKm.toFixed(2)} min/km Pace`,
            `${movingTimeMin}-Minute Run`,
          ];}
      }
    }

    return { analysis, suggestions };
  }

  private transform(value: number, args?: string): string {
    // Hours, minutes and seconds
    if (isNaN(value)) {
      return args ? '-' + args : '-';
    }
    const hrs = Math.floor(value / 3600);
    const mins = Math.floor((value % 3600) / 60);
    const secs = value % 60;

    // Output like "1:01" or "4:03:59" or "123:03:59"
    let ret = '';
    if (args) {
      if (hrs > 0) {
        ret += '' + (hrs < 10 ? '0' : '') + hrs + ':' + (mins < 10 ? '0' : '');
      }
    } else {
      if (hrs > 0) {
        ret += '' + hrs + ':' + (mins < 10 ? '0' : '');
      }
    }
    ret += '' + mins + ':' + (secs < 10 ? '0' : '');
    ret += '' + secs;
    if (args) {
      return ret + ' ' + args;
    }
    return ret;
  }

  private mergeSimilarLaps(laps: any[], paceThreshold: number = 0.05): any[] {
    const merged: any[] = [];
    let currentLap = { ...laps[0] };

    for (let i = 1; i < laps.length; i++) {
      const lap = laps[i];
      const paceDifference = Math.abs(
        lap.elapsed_time / lap.distance -
          currentLap.elapsed_time / currentLap.distance
      );

      if (paceDifference <= paceThreshold) {
        currentLap.distance += lap.distance;
        currentLap.elapsed_time += lap.elapsed_time;
        currentLap.average_speed =
          currentLap.distance / currentLap.elapsed_time;
      } else {
        merged.push({ ...currentLap });
        currentLap = { ...lap };
      }
    }

    merged.push({ ...currentLap });
    return merged;
  }

  // Categorize laps into warmup, cooldown, effort, and rest
  private categorizeLaps(laps: any[]): any {
    const averagePace =
      laps.reduce((sum, lap) => sum + lap.elapsed_time / lap.distance, 0) /
      laps.length;

    const categorized = {
      warmup: [],
      cooldown: [],
      effort: [],
      rest: [],
      progressive: true,
    };

    const effortThreshold = averagePace * 0.9;
    const restThreshold = averagePace * 1.2;

    laps.forEach((lap, index) => {
      const pace = lap.elapsed_time / lap.distance;

      if (pace < effortThreshold) {
        categorized.effort.push(lap);
      } else if (pace > restThreshold) {
        categorized.rest.push(lap);
      } else if (index === 0 || index === 1) {
        categorized.warmup.push(lap);
      } else if (index >= laps.length - 2) {
        categorized.cooldown.push(lap);
      }

      if (
        index > 0 &&
        pace < laps[index - 1].elapsed_time / laps[index - 1].distance
      ) {
        categorized.progressive = false;
      }
    });

    return categorized;
  }

  // Detect special workout types based on pace patterns
  private detectSpecialWorkoutType(
    laps: any[],
    categorizedLaps: any
  ): string | null {
    const effortPaces = categorizedLaps.effort.map(
      (lap) => lap.elapsed_time / lap.distance
    );

    if (categorizedLaps.effort.length > 1 && categorizedLaps.rest.length > 1) {
      // Fartlek: Alternating fast and slow paces
      if (categorizedLaps.effort.length === categorizedLaps.rest.length) {
        return 'Fartlek';
      }

      // On/Off Training: Alternating fixed pace fast/slow efforts
      const paceVariance = Math.max(...effortPaces) - Math.min(...effortPaces);
      if (paceVariance < 0.1) {
        return 'On/Off';
      }
    }

    // Hill Repeats: Significant elevation gain in effort laps
    if (categorizedLaps.effort.some((lap) => lap.total_elevation_gain > 50)) {
      return 'Hill Repeats';
    }

    // Pyramid Training: Increasing and then decreasing lap durations
    const durations = categorizedLaps.effort.map((lap) => lap.elapsed_time);
    if (
      durations
        .slice(0, Math.floor(durations.length / 2))
        .every((d, i) => d < durations[i + 1]) &&
      durations
        .slice(Math.ceil(durations.length / 2))
        .every((d, i) => d > durations[i + 1])
    ) {
      return 'Pyramid';
    }

    // Progressive Run: Consistently faster laps
    const isProgressive = durations.every(
      (d, i, arr) => i === 0 || d <= arr[i - 1]
    );
    if (isProgressive) {
      return 'Progressive';
    }

    return null;
  }
}
